import get from 'lodash/get';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import DataTable from 'components/DataTable';
import { handleErrorResponse, parseJSON } from 'utils/general';
import MKTypography from 'components/MaterialKit/MKTypography';
import { getData } from 'api/general';
import { useAuth } from 'contexts/auth';
import Image from 'components/Image';
import moment from 'moment';
import { DATE_FORMAT_DATE_ONLY_DEFAULT } from 'utils/datetime';

const ActivityTableSection = ({
  table_name,
  user_relation_table_name,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  container_props,
  ...props
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const { auth, setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [userRelationData, setUserRelationData] = useState([]);
  const [formattedTableColumns, setFormattedTableColumns] = useState([]);

  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMobileChange = (event) => {
      setIsMobile(event.matches);
    };
    mobileMediaQuery.addEventListener('change', handleMobileChange);
    setIsMobile(mobileMediaQuery.matches);
    return () => {
      mobileMediaQuery.removeEventListener('change', handleMobileChange);
    };
  }, []);

  const fetchActivitiesFromApi = useCallback(() => {
    const dataParams = {
      ...extra_params,
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
    };
    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data }) => {
        const processData = data.map((datum) => {
          let result = { ...datum };
          const activityId = datum.activity_id;
          userRelationData.forEach((relationDatum) => {
            if (relationDatum.activity_id === activityId) {
              result = {
                ...result,
                ...relationDatum,
              };
            }
          });
          return result;
        });
        setRowsData(processData);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [extra_params, setAuth, sortBy, sortDirection, table_name, userRelationData]);

  const fetchUserRelationFromApi = useCallback(() => {
    const username = get(auth, ['user', 'username']);
    const params = {
      user: username,
    };
    return getData(`/${user_relation_table_name}`, params, 'bearer')
      .then(({ data }) => {
        const filteredData = data.map((datum) => {
          const result = {
            activity_id: datum.activity,
            username: datum.user,
          };
          (column_mapping_config || []).forEach((key) => {
            result[key] = datum[key];
          });
          return result;
        });
        setUserRelationData(filteredData);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, column_mapping_config, setAuth, user_relation_table_name]);

  useEffect(() => {
    if (user_relation_table_name && auth?.user?.username) {
      fetchUserRelationFromApi();
    }
  }, [auth?.user, fetchUserRelationFromApi, user_relation_table_name, value_mapping_config]);

  useEffect(() => {
    if (table_name && userRelationData) {
      fetchActivitiesFromApi();
    }
  }, [fetchActivitiesFromApi, table_name, userRelationData]);

  const onClickSort = useCallback((headerName) => {
    setSortBy(headerName);
    setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const columnName in value_mapping_config) {
      // eslint-disable-next-line no-loop-func
      (columns_config || []).forEach((column) => {
        if (column.field === columnName) {
          // eslint-disable-next-line no-param-reassign
          column.renderContent = (activity) => {
            const type = value_mapping_config[columnName]?.type;
            if (activity[columnName] || activity[columnName] !== null) {
              return (
                <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
                  {type && type.includes('image') && (
                    <Image
                      width={value_mapping_config[columnName]?.image_width}
                      src={value_mapping_config[columnName][`${activity[columnName]}_image`] || value_mapping_config[columnName]?.image}
                      style={{ marginRight: '0.25rem' }}
                      {...(type.includes('url') && {
                        onClick: () => window.open(activity[columnName], '_blank').focus(),
                        style: { cursor: 'pointer' },
                      })}
                    />
                  )}
                  {type && type.includes('string') && (
                    <MKTypography fontSize="0.875rem" color={value_mapping_config[columnName][`${activity[columnName]}_color`]}>
                      {value_mapping_config[columnName][activity[columnName]]}
                    </MKTypography>
                  )}
                  {type && type.includes('timestamp') && (
                    <MKTypography fontSize="0.875rem" color={value_mapping_config[columnName]?.color}>
                      {moment(activity[columnName]).format(value_mapping_config[columnName]?.format || DATE_FORMAT_DATE_ONLY_DEFAULT)}
                    </MKTypography>
                  )}
                </MKBox>
              );
            }
            return ' ';
          };
        }
      });
    }
    setFormattedTableColumns(columns_config);
  }, [columns_config, value_mapping_config]);

  const renderData = () => {
    return rowsData.map((datum) => {
      return (
        <MKBox display="flex" flexDirection="column" mb="2rem">
          {columns_config.map((column) => {
            const content = [];
            const field = column?.field;
            // eslint-disable-next-line no-prototype-builtins
            if (value_mapping_config.hasOwnProperty(field)) {
              const type = value_mapping_config[field]?.type;
              if (type && type.includes('image')) {
                content.push((
                  <Image
                    width={value_mapping_config[field]?.image_width}
                    src={value_mapping_config[field][`${datum[field]}_image`] || value_mapping_config[field]?.image}
                    style={{ marginRight: '0.25rem' }}
                    {...(type.includes('url') && {
                      onClick: () => window.open(datum[field], '_blank').focus(),
                      style: { cursor: 'pointer' },
                    })}
                  />
                ));
              }
              if (type && type.includes('string')) {
                content.push((
                  <MKTypography color={value_mapping_config[field][`${datum[field]}_color`]}>
                    {value_mapping_config[field][datum[field]]}
                  </MKTypography>
                ));
              }
            }
            return (
              <MKBox display="flex" flexDirection="row" mb="0.2rem">
                <MKBox flex={1} sx={{ backgroundColor: header_background_color }}>
                  <MKTypography padding="0.25rem" color={header_font_color} textAlign="center">{column.label}</MKTypography>
                </MKBox>
                <MKBox flex={2} ml="0.2rem" justifyContent="center" sx={{ backgroundColor: even_row_background_color }}>
                  <MKBox display="flex" padding="0.25rem" color={even_row_font_color} justifyContent="center">{content.length > 0 ? content : datum[column.field]}</MKBox>
                </MKBox>
              </MKBox>
            );
          })}
        </MKBox>
      );
    });
  };

  return (
    <MKBox
      sx={{
        ...parseJSON(container_props, {}),
      }}
      {...props}
    >
      {isMobile ? (
        <MKBox>
          {renderData()}
        </MKBox>
      ) : (
        <DataTable
          data={rowsData}
          columns={formattedTableColumns}
          headerBackgroundColor={header_background_color}
          headerFontColor={header_font_color}
          oddRowBackgroundColor={odd_row_background_color}
          oddRowFontColor={odd_row_font_color}
          evenRowBackgroundColor={even_row_background_color}
          evenRowFontColor={even_row_font_color}
          idField="dharma_id"
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onClickSort}
          paginationDisabled
          // onClickRow={editable ? onClickEdit : null}
        />
      )}
    </MKBox>
  );
};

ActivityTableSection.propTypes = {
  table_name: PropTypes.string,
  user_relation_table_name: PropTypes.string,
  columns_config: PropTypes.array,
  column_mapping_config: PropTypes.array,
  value_mapping_config: PropTypes.object,
  odd_row_background_color: PropTypes.string,
  even_row_background_color: PropTypes.string,
  odd_row_font_color: PropTypes.string,
  even_row_font_color: PropTypes.string,
  header_background_color: PropTypes.string,
  header_font_color: PropTypes.string,
  page_options: PropTypes.array,
  extra_params: PropTypes.object,
  container_props: PropTypes.string,
};

ActivityTableSection.defaultProps = {
  table_name: '',
  user_relation_table_name: '',
  columns_config: [],
  column_mapping_config: [],
  value_mapping_config: {},
  odd_row_background_color: '',
  even_row_background_color: '',
  odd_row_font_color: '',
  even_row_font_color: '',
  header_background_color: '',
  header_font_color: '',
  page_options: [],
  extra_params: {},
  container_props: '',
};

export default ActivityTableSection;
